import * as Sentry from "@sentry/react";

const SENSITIVE_KEY_PATTERN =
  /(token|secret|password|authorization|cookie|key|session|jwt|bearer)/i;
const SENSITIVE_QUERY_PATTERN =
  /(token|secret|password|key|session|code|jwt|auth)/i;

const redactValue = (value) => {
  if (!value || typeof value !== "object") return value;

  if (Array.isArray(value)) {
    return value.map((entry) => redactValue(entry));
  }

  const output = {};
  Object.entries(value).forEach(([key, entry]) => {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      output[key] = "[REDACTED]";
      return;
    }
    output[key] = redactValue(entry);
  });

  return output;
};

const sanitizeUrl = (raw) => {
  if (!raw || typeof raw !== "string") return raw;
  try {
    const url = new URL(raw, window.location.origin);
    for (const key of Array.from(url.searchParams.keys())) {
      if (SENSITIVE_QUERY_PATTERN.test(key)) {
        url.searchParams.set(key, "[REDACTED]");
      }
    }
    return url.toString();
  } catch {
    return raw;
  }
};

const beforeSend = (event) => {
  const nextEvent = redactValue(event);

  if (nextEvent?.request?.url) {
    nextEvent.request.url = sanitizeUrl(nextEvent.request.url);
  }

  if (Array.isArray(nextEvent?.breadcrumbs)) {
    nextEvent.breadcrumbs = nextEvent.breadcrumbs.map((crumb) => {
      if (crumb?.data?.url) {
        return {
          ...crumb,
          data: {
            ...crumb.data,
            url: sanitizeUrl(crumb.data.url),
          },
        };
      }
      return crumb;
    });
  }

  return nextEvent;
};

export const initSentry = () => {
  const dsn = (import.meta.env.VITE_SENTRY_DSN || "").trim();
  if (!dsn) return false;

  const environment = (import.meta.env.VITE_SENTRY_ENVIRONMENT || "").trim() ||
    import.meta.env.MODE;
  const release = (import.meta.env.VITE_SENTRY_RELEASE || "").trim() ||
    undefined;

  const sessionReplaySampleRate = Number(
    import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE ?? "0.10",
  );
  const onErrorReplaySampleRate = Number(
    import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE ?? "1.0",
  );

  Sentry.init({
    dsn,
    environment,
    release,
    tracesSampleRate: 0,
    replaysSessionSampleRate: Number.isFinite(sessionReplaySampleRate)
      ? sessionReplaySampleRate
      : 0.1,
    replaysOnErrorSampleRate: Number.isFinite(onErrorReplaySampleRate)
      ? onErrorReplaySampleRate
      : 1,
    integrations: [Sentry.replayIntegration()],
    beforeSend,
    sendDefaultPii: false,
  });

  return true;
};

export const setSentryUser = (user) => {
  if (user?.id || user?.email) {
    Sentry.setUser({
      id: user.id ? String(user.id) : undefined,
      email: user.email ? String(user.email) : undefined,
    });
    return;
  }
  Sentry.setUser(null);
};

export { Sentry };

